/**
 * Conteúdo da aba Faturas no relatório.
 * Exibe filtros, KPIs financeiros e tabela de faturas (independente de FaturasTab).
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Skeleton,
  useTheme,
  alpha,
  Avatar,
  TextField,
  MenuItem,
  Button,
  InputAdornment,
  IconButton,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  TableFooter,
  Stack,
  Chip,
} from '@mui/material';
import {
  Receipt,
  AttachMoney,
  CheckCircle,
  HourglassEmpty,
  Warning,
  Cancel,
  Search,
  Clear,
  FilterList,
  ExpandMore,
  ExpandLess,
  PictureAsPdf,
} from '@mui/icons-material';
import { formatarData, formatarValor } from '../../utils/formatToView';
import {
  fmtInt,
  useRelatorioCores,
  parseYmdToLocalDate,
  dateToYmd,
  quickFilterOptions,
} from './relatorioUtils';
import { InvoiceStatusChip } from '../../utils/faturaStatusUtils';
import { Invoice } from '../../types';
import type { FiltersFaturas } from './relatorioTypes';
import { estadosBR } from './relatorioTypes';
import SysDateRangePicker from '../../components/SysDateRangePicker';
import InvoiceViewModal from '../../components/modals/InvoiceViewModal';

const tipoLabel: Record<string, string> = {
  mensalidade: 'Mensalidade',
  servicos: 'Serviços',
  outros: 'Outros',
};

export interface FinSummary {
  totalValor: number;
  totalPago: number;
  totalAberto: number;
  totalVencido: number;
  totalCancelado: number;
  total: number;
}

export interface RelatorioFaturasTabProps {
  data: Invoice[];
  filters: FiltersFaturas;
  onFilterChange: (updates: Partial<FiltersFaturas>) => void;
  onClearFilters: () => void;
  finSummary: FinSummary;
  isLoading: boolean;
  page: number;
  rowsPerPage: number;
  orderBy: string;
  orderDir: 'asc' | 'desc';
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  onSort: (col: string) => void;
  sortData: <T>(data: T[]) => T[];
  paginateData: <T>(data: T[]) => T[];
  onExportPdf?: (ids: number[]) => Promise<void>;
}

const RelatorioFaturasTab: React.FC<RelatorioFaturasTabProps> = ({
  data,
  filters,
  onFilterChange,
  onClearFilters,
  finSummary,
  isLoading,
  page,
  rowsPerPage,
  orderBy,
  orderDir,
  onPageChange,
  onRowsPerPageChange,
  onSort,
  sortData,
  paginateData,
  onExportPdf,
}) => {
  const theme = useTheme();
  const cores = useRelatorioCores();
  const faturasColor = cores.bloqueado;
  const [filtersOpen, setFiltersOpen] = React.useState(false);
  const [exportingPdf, setExportingPdf] = React.useState(false);
  const hasActiveFilters = Object.values(filters).some((v) => v !== '');
  const navigate = useNavigate();
  const [selectedInvoiceId, setSelectedInvoiceId] = React.useState<number | null>(null);

  const handleExportPdf = async () => {
    if (!onExportPdf || data.length === 0) return;
    setExportingPdf(true);
    try {
      await onExportPdf(data.map((f) => f.id));
    } catch (err) {
      console.error('Erro ao exportar PDF:', err);
    } finally {
      setExportingPdf(false);
    }
  };

  const applyQuickFilter = (filterValue: string) => {
    const selectedFilter = quickFilterOptions.find((opt) => opt.value === filterValue);
    if (selectedFilter) {
      const range = selectedFilter.getRange();
      onFilterChange({
        dataInicio: range.startDate ? dateToYmd(range.startDate) : '',
        dataFim: range.endDate ? dateToYmd(range.endDate) : '',
        quickFilter: filterValue,
      });
    }
  };

  const sorted = sortData(data);
  const rows = paginateData(sorted);

  const totalValorPagina = rows.reduce((acc: number, f: Invoice) => acc + (Number(f.valor) || 0), 0);
  const totalPagoPagina = rows.reduce((acc: number, f: Invoice) => {
    const vPago = (f as any).valor_pago || 0;
    return acc + Number(vPago);
  }, 0);

  const columns = [
    { id: 'id', label: 'ID', width: 60 },
    { id: 'cliente', label: 'Cliente' },
    { id: 'descricao', label: 'Descrição' },
    { id: 'tipo', label: 'Tipo', width: 100 },
    { id: 'data_vencimento', label: 'Vencimento', width: 100 },
    { id: 'valor', label: 'Valor', width: 100 },
    { id: 'status', label: 'Status', width: 110 },
    { id: 'data_pagamento', label: 'Pagamento', width: 100 },
    { id: 'valor_pago', label: 'Valor Pago', width: 120 },
    { id: 'forma_pagamento', label: 'Forma Pagamento', width: 120 },
    { id: 'mes_referencia', label: 'Referência', width: 90 },
  ];

  const kpis = [
    { titulo: 'Total Faturas', valor: fmtInt(finSummary.total), icon: <Receipt />, cor: cores.primario },
    { titulo: 'Valor Total', valor: formatarValor(finSummary.totalValor), icon: <AttachMoney />, cor: cores.secundario },
    { titulo: 'Total Recebido', valor: formatarValor(finSummary.totalPago), icon: <CheckCircle />, cor: cores.ativo },
    { titulo: 'Em Aberto', valor: formatarValor(finSummary.totalAberto), icon: <HourglassEmpty />, cor: cores.aguardando },
    { titulo: 'Vencido', valor: formatarValor(finSummary.totalVencido), icon: <Warning />, cor: cores.cancelado },
    { titulo: 'Cancelado', valor: formatarValor(finSummary.totalCancelado), icon: <Cancel />, cor: '#9e9e9e' },
  ];

  return (
    <Box>
      <Paper elevation={2} sx={{ mb: 1, borderRadius: 2, overflow: 'hidden' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 2,
            py: 1,
            bgcolor: alpha(faturasColor, 0.08),
            cursor: 'pointer',
          }}
          onClick={() => setFiltersOpen(!filtersOpen)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterList sx={{ fontSize: 18, color: faturasColor }} />
            <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.85rem' }}>
              Filtros
            </Typography>
            {hasActiveFilters && (
              <Typography component="span" variant="caption" color="primary" fontWeight={600}>
                Filtros ativos
              </Typography>
            )}
            <Typography component="span" variant="caption" color="text.secondary" sx={{ fontSize: '0.74rem' }}>
              {isLoading ? (
                <Skeleton width={100} sx={{ display: 'inline-block' }} />
              ) : (
                <>
                  <strong>{fmtInt(data.length)}</strong> registro(s) encontrado(s)
                </>
              )}
            </Typography>
          </Box>
          <IconButton size="small">{filtersOpen ? <ExpandLess /> : <ExpandMore />}</IconButton>
        </Box>
        <Collapse in={filtersOpen}>
          <Box sx={{ p: 2, pt: 1.5 }}>
            {/* Filtros Rápidos */}
            <Box mb={2}>
              <Typography variant="subtitle2" color="text.secondary" mb={1} sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                Períodos Predefinidos
              </Typography>
              <Stack direction="row" spacing={0.8} flexWrap="wrap" gap={0.8}>
                {quickFilterOptions.map((option) => (
                  <Chip
                    key={option.value}
                    label={option.label}
                    variant={filters.quickFilter === option.value ? 'filled' : 'outlined'}
                    color={filters.quickFilter === option.value ? 'primary' : 'default'}
                    onClick={() => applyQuickFilter(option.value)}
                    clickable
                    size="small"
                    sx={{
                      fontSize: '0.75rem',
                      height: 26,
                      borderRadius: '6px',
                      '&:hover': {
                        bgcolor: filters.quickFilter === option.value ? theme.palette.primary.main : alpha(theme.palette.primary.main, 0.08),
                      },
                    }}
                  />
                ))}
              </Stack>
            </Box>            
            <Grid container spacing={1.5}>
              <Grid item xs={6} sm={6} md={4}>
                <SysDateRangePicker
                  label="Período de Criação"
                  value={{
                    startDate: filters.dataCriacaoInicio ? parseYmdToLocalDate(filters.dataCriacaoInicio) : null,
                    endDate: filters.dataCriacaoFim ? parseYmdToLocalDate(filters.dataCriacaoFim) : null,
                  }}
                  onChange={(range) =>
                    onFilterChange({
                      dataCriacaoInicio: dateToYmd(range.startDate),
                      dataCriacaoFim: dateToYmd(range.endDate),
                    })
                  }
                />
              </Grid>
              <Grid item xs={6} sm={6} md={4}>
                <SysDateRangePicker
                  label="Período data de vencimento"
                  value={{
                    startDate: filters.dataInicio ? parseYmdToLocalDate(filters.dataInicio) : null,
                    endDate: filters.dataFim ? parseYmdToLocalDate(filters.dataFim) : null,
                  }}
                  onChange={(range) =>
                    onFilterChange({
                      dataInicio: dateToYmd(range.startDate),
                      dataFim: dateToYmd(range.endDate),
                    })
                  }
                />
              </Grid>
              <Grid item xs={6} sm={6} md={4}>
                <SysDateRangePicker
                  label="Período data de pagamento"
                  value={{
                    startDate: filters.dataPagamentoInicio ? parseYmdToLocalDate(filters.dataPagamentoInicio) : null,
                    endDate: filters.dataPagamentoFim ? parseYmdToLocalDate(filters.dataPagamentoFim) : null,
                  }}
                  onChange={(range) =>
                    onFilterChange({
                      dataPagamentoInicio: dateToYmd(range.startDate),
                      dataPagamentoFim: dateToYmd(range.endDate),
                    })
                  }
                />
              </Grid>
              <Grid item xs={4} sm={3} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  select
                  label="Estado"
                  value={filters.estado}
                  onChange={(e) => onFilterChange({ estado: e.target.value })}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {estadosBR.map((uf) => (
                    <MenuItem key={uf} value={uf}>
                      {uf}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={8} sm={3} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Cidade"
                  value={filters.cidade}
                  onChange={(e) => onFilterChange({ cidade: e.target.value })}
                />
              </Grid>
              <Grid item xs={6} sm={3} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Bairro"
                  value={filters.bairro}
                  onChange={(e) => onFilterChange({ bairro: e.target.value })}
                />
              </Grid>
              <Grid item xs={6} sm={3} md={5.4}>
                <TextField
                fullWidth
                size="small"
                placeholder="Buscar por ID, descrição, cliente..."
                value={filters.search}
                onChange={(e) => onFilterChange({ search: e.target.value })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ fontSize: 18, color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                  endAdornment: filters.search ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => onFilterChange({ search: '' })}>
                        <Clear sx={{ fontSize: 16 }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
              </Grid>
              <Grid item xs={6} sm={3} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  select
                  label="Status Fatura"
                  value={filters.statusFatura}
                  onChange={(e) => onFilterChange({ statusFatura: e.target.value })}
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="aberto">Aberto</MenuItem>
                  <MenuItem value="pago">Pago</MenuItem>
                  <MenuItem value="vencido">Vencido</MenuItem>
                  <MenuItem value="parcialmente_pago">Parc. Pago</MenuItem>
                  <MenuItem value="cancelado">Cancelado</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={6} sm={3} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  select
                  label="Tipo Fatura"
                  value={filters.tipoFatura}
                  onChange={(e) => onFilterChange({ tipoFatura: e.target.value })}
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="mensalidade">Mensalidade</MenuItem>
                  <MenuItem value="servicos">Serviços</MenuItem>
                  <MenuItem value="outros">Outros</MenuItem>
                </TextField>
              </Grid>
              
              <Grid item xs={12} sm={3} md={2.6} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                {onExportPdf && (
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={exportingPdf ? null : <PictureAsPdf sx={{ fontSize: 16 }} />}
                    onClick={handleExportPdf}
                    disabled={isLoading || data.length === 0 || exportingPdf}
                    sx={{ textTransform: 'none', fontSize: '0.78rem' }}
                  >
                    {exportingPdf ? 'Exportando...' : 'Exportar PDF'}
                  </Button>
                )}
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Clear sx={{ fontSize: 16 }} />}
                  onClick={onClearFilters}
                  disabled={!hasActiveFilters}
                  sx={{ textTransform: 'none', fontSize: '0.78rem' }}
                >
                  Limpar Filtros
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </Paper>

      <Grid container spacing={1.5} sx={{ mb: 1 }}>
        {kpis.map((kpi) => (
          <Grid item xs={6} sm={4} md={2} key={kpi.titulo}>
            <Paper
              elevation={2}
              sx={{
                borderRadius: 2,
                overflow: 'hidden',
                transition: 'box-shadow 0.2s, transform 0.15s',
                '&:hover': { boxShadow: theme.shadows[6], transform: 'translateY(-2px)' },
              }}
            >
              <Box sx={{ height: 3, background: `linear-gradient(90deg, ${kpi.cor} 0%, ${alpha(kpi.cor, 0.4)} 100%)` }} />
              <Box sx={{ p: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {kpi.titulo}
                  </Typography>
                  <Avatar sx={{ bgcolor: alpha(kpi.cor, 0.12), color: kpi.cor, width: 28, height: 28 }}>
                    {React.cloneElement(kpi.icon, { sx: { fontSize: 15 } })}
                  </Avatar>
                </Box>
                {isLoading ? (
                  <Skeleton width="60%" height={28} />
                ) : (
                  <Typography variant="h6" fontWeight={800} color="text.primary" sx={{ lineHeight: 1.1, fontSize: '1rem' }}>
                    {kpi.valor}
                  </Typography>
                )}
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow
                sx={{
                  '& .MuiTableCell-head': {
                    bgcolor: (t) => `color-mix(in srgb, ${faturasColor} 12%, ${t.palette.background.paper})`,
                    fontWeight: 700,
                    fontSize: '0.73rem',
                    py: 0.8,
                    borderBottom: `2px solid ${alpha(faturasColor, 0.2)}`,
                  },
                }}
              >
                {columns.map((col) => (
                  <TableCell key={col.id} sx={{ width: col.width }}>
                    <TableSortLabel
                      active={orderBy === col.id}
                      direction={orderBy === col.id ? orderDir : 'asc'}
                      onClick={() => onSort(col.id)}
                    >
                      {col.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <TableRow key={i}>
                    {columns.map((col) => (
                      <TableCell key={col.id}>
                        <Skeleton />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.disabled">
                      Nenhum registro encontrado
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((f) => (
                  <TableRow key={f.id} hover sx={{ '&:hover': { bgcolor: alpha(faturasColor, 0.03) } }}>
                    <TableCell
                      sx={{
                        py: 0.5,
                        fontSize: '0.78rem',
                        cursor: 'pointer'
                      }}
                      onClick={() => setSelectedInvoiceId(f.id)}
                    >
                      #{f.id}
                    </TableCell>
                    <TableCell
                      sx={{
                        py: 0.5,
                        fontSize: '0.78rem',
                        whiteSpace: 'nowrap',
                        cursor: 'pointer'
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/clients/${f.cliente_id}`);
                      }}
                    >
                      {f.cliente?.nome || `#${f.cliente_id}`}
                    </TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '0.78rem',
                          maxWidth: 200,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {f.descricao}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>{tipoLabel[f.tipo] || f.tipo}</TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>{formatarData(f.data_vencimento)}</TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem', fontWeight: 600 }}>{formatarValor(f.valor)}</TableCell>
                    <TableCell sx={{ py: 0.5 }}><InvoiceStatusChip status={f.status} /></TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                      {f.data_pagamento ? formatarData(f.data_pagamento) : '-'}
                    </TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem', fontWeight: 600 }}>
                      {formatarValor((f as any).valor_pago || 0)}
                    </TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>{f.forma_pagamento || '-'}</TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>{f.mes_referencia}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
            {!isLoading && data.length > 0 && (
              <TableFooter sx={{ bgcolor: 'background.paper' }}>
                <TableRow
                  sx={{
                    '& .MuiTableCell-root': {
                      bgcolor: (t) => `color-mix(in srgb, ${faturasColor} 15%, ${t.palette.background.paper})`,
                      fontWeight: 800,
                      fontSize: '0.78rem',
                      borderTop: `2px solid ${alpha(faturasColor, 0.3)}`,
                      py: 1.2,
                      color: 'text.primary',
                    },
                  }}
                >
                  <TableCell colSpan={5} align="right" sx={{ letterSpacing: '0.05em' }}>
                    TOTAL
                  </TableCell>
                  <TableCell>
                    {formatarValor(totalValorPagina)}
                  </TableCell>
                  <TableCell colSpan={2} />
                  <TableCell>
                    {formatarValor(totalPagoPagina)}
                  </TableCell>
                  <TableCell colSpan={2} />
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </TableContainer>
        {data.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 0.8,
              px: 2,
              bgcolor: alpha(faturasColor, 0.04),
              borderTop: `1px solid ${alpha(faturasColor, 0.15)}`,
            }}
          >
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={data.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_e, p) => onPageChange(p)}
              onRowsPerPageChange={(e) => {
                onRowsPerPageChange(parseInt(e.target.value, 10));
                onPageChange(0);
              }}
              labelRowsPerPage="Linhas por pág:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              sx={{
                width: '100%',
                '.MuiTablePagination-toolbar': {
                  minHeight: 32,
                  flexWrap: 'nowrap',
                  justifyContent: 'center',
                  gap: 1,
                  p: 0,
                  '& .MuiTablePagination-spacer': { display: 'none' },
                },
                '.MuiTablePagination-selectLabel': { m: 0, fontSize: '0.78rem' },
                '.MuiTablePagination-select': { py: 0.5, pr: 2.5, fontSize: '0.78rem' },
                '.MuiTablePagination-displayedRows': { m: 0, fontSize: '0.78rem' },
                '.MuiTablePagination-actions': { ml: 0.5, '& .MuiIconButton-root': { p: 0.5 } },
              }}
            />
          </Box>
        )}
      </Paper>

      {/* Modal de Detalhes da Fatura */}
      {selectedInvoiceId && (
        <InvoiceViewModal
          open={!!selectedInvoiceId}
          invoice_id={selectedInvoiceId}
          onClose={() => setSelectedInvoiceId(null)}
        />
      )}
    </Box>
  );
};

export default RelatorioFaturasTab;
